import { useEffect } from "react";
import { useRoute } from "wouter";
import { useEventInterested, type EventRsvpItem } from "../lib/api/useEventRsvp";
import { useAuth } from "../lib/platform";
import { PageHeader } from "../components/ux/PageHeader";
import { ProfileImage } from "../components/ProfileImage";
import { PublicProfileButton } from "../components/PublicProfileModal";
import { OnboardingProvider, useOnboardingGate } from "../components/OnboardingContext";
import { Loader } from "../components/Loader";
import Logo from "@/components/Logo";

export default function EventInterested() {
  // Provider so the signed-out gate opens the onboarding explainer.
  return (
    <OnboardingProvider>
      <EventInterestedContent />
    </OnboardingProvider>
  );
}

function PeopleList({
  people,
  interactive,
}: {
  people: EventRsvpItem[];
  interactive?: boolean;
}) {
  return (
    <div className="divide-y divide-base-200 dark:divide-neutral-800">
      {people.map((r) => {
        const name = r.lastName
          ? `${r.firstName} ${r.lastName.charAt(0).toUpperCase()}.`
          : r.firstName;
        const inner = (
          <>
            <ProfileImage
              size="md"
              user={{ firstName: r.firstName, lastName: r.lastName, profileImageUrl: r.profileImageUrl }}
            />
            <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">{name}</span>
          </>
        );
        return interactive ? (
          <PublicProfileButton
            key={r.id}
            userId={r.id}
            ariaLabel={`View ${r.firstName}'s profile`}
            className="flex items-center gap-3 w-full text-left py-3 hover:opacity-80 transition-opacity"
          >
            {inner}
          </PublicProfileButton>
        ) : (
          <div key={r.id} className="flex items-center gap-3 py-3">
            {inner}
          </div>
        );
      })}
    </div>
  );
}

function EventInterestedContent() {
  const [, params] = useRoute("/events/:slug/interested");
  const slug = params?.slug ?? null;
  const { isSignedIn, openSignIn } = useAuth();
  const onboarding = useOnboardingGate();
  const gateSignIn = () =>
    onboarding ? onboarding.openOnboarding(slug ? `/events/${slug}/interested` : undefined) : openSignIn();

  const { data, isLoading } = useEventInterested(slug);
  const people = data?.success ? data.data ?? [] : [];

  useEffect(() => {
    document.title = "Interested — Outclose";
    return () => { document.title = "Outclose"; };
  }, []);

  return (
    <div className="min-h-screen pb-24 pt-4">
      <Logo withIcon={true} className="md:hidden justify-center pt-4 mb-4" />

      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6">
        <PageHeader
          backHref={slug ? `/events/${slug}` : "/events"}
          backLabel={"Back to event"}
        />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : people.length === 0 ? (
          <p className="text-center text-neutral-500 dark:text-neutral-400 py-16">
            No one's marked interested yet.
          </p>
        ) : isSignedIn ? (
          <PeopleList people={people} interactive />
        ) : (
          <div className="relative">
            <div className="blur-[6px] pointer-events-none select-none" aria-hidden>
              <PeopleList people={people} />
            </div>
            <div className="absolute inset-0 flex items-start justify-center pt-16">
              <button
                type="button"
                onClick={gateSignIn}
                className="px-4 py-2 rounded-full bg-white/95 dark:bg-neutral-900/90 text-neutral-900 dark:text-white text-sm font-semibold shadow-lg backdrop-blur ring-1 ring-black/10"
              >
                Sign in to see who's interested
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
